import { useEffect, useState } from 'react';

import { useSetIssueTypeFields } from '../providers/BulkCreateContextProvider.tsx';
import {
	useAvailableSitesState,
	useIssueTypesState,
	useMappingsActions,
	useProjectsState,
} from '../providers/BulkConfigureContextProvider.tsx';

import { fetchIssuesMetadata } from './apiUtils.ts';

export const useGetIssueTypeFields = (): {
	isLoading: boolean;
	error: string | null;
} => {
	const setIssueTypeFields = useSetIssueTypeFields();
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const { selectedAvailableSite } = useAvailableSitesState();
	const { selectedProject } = useProjectsState();
	const { selectedIssueType } = useIssueTypesState();
	const { setOptionsIssueTypeFields, setLoadingIssueTypeFields, setFieldAllowedValues } =
		useMappingsActions();

	useEffect(() => {
		const fetchIssueMetadata = async () => {
			if (!selectedAvailableSite || !selectedProject || !selectedIssueType) {
				return;
			}

			setIsLoading(true);
			setLoadingIssueTypeFields(true);

			try {
				const data = await fetchIssuesMetadata({
					cloudId: selectedAvailableSite.cloudId,
					projectId: selectedProject.id,
					issueTypeId: selectedIssueType.id,
				});
				const mappingFieldAllowedValues = new Map<string, unknown[]>();
				if (data) {
					const requiredFields = [];
					const optionalFields = [];
					for (const field of data.fields) {
						if (field.required) {
							requiredFields.push(field);
						} else {
							optionalFields.push(field);
						}

						// Store allowedValues for any field that has them
						if (field.allowedValues && Array.isArray(field.allowedValues)) {
							mappingFieldAllowedValues.set(field.fieldId, field.allowedValues);
						}
					}
					void setIssueTypeFields({ requiredFields, optionalFields });
					void setOptionsIssueTypeFields([...requiredFields, ...optionalFields]);
					void setFieldAllowedValues(mappingFieldAllowedValues);
				}
			} catch (e: Error | unknown) {
				if (isError(e)) {
					setError(e.message);
				}
			} finally {
				setIsLoading(false);
				setLoadingIssueTypeFields(false);
			}
		};
		void fetchIssueMetadata();
	}, [
		selectedAvailableSite,
		selectedProject,
		selectedIssueType,
		setIssueTypeFields,
		setOptionsIssueTypeFields,
		setLoadingIssueTypeFields,
		setFieldAllowedValues,
	]);

	return { isLoading, error };
};

const isError = (error: Error | unknown): error is Error => {
	return error instanceof Error;
};
